import React, { memo, useState } from 'react';
import {
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  Chip,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

const CheckboxGroup = memo(({ 
  title, 
  items, 
  selectedItems, 
  onChange, 
  name,
  showSelectAll = false,
  required = false,
  error = false,
  helperText = "",
  layoutMode = 'list', // 'list' o 'grid'
  columns = 2, // Número de columnas cuando está en modo grid
  showLayoutToggle = false // Mostrar botones para cambiar el layout
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [currentLayout, setCurrentLayout] = useState(layoutMode);
  
  const filteredItems = items.filter(item => 
    item.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const selectedItemsData = items.filter(item => selectedItems.includes(item.id));
  
  const allSelected = items.length > 0 && items.every(item => selectedItems.includes(item.id));
  
  const handleSelectAll = () => {
    if (allSelected) {
      items.forEach(item => {
        if (selectedItems.includes(item.id)) {
          onChange({ target: { name, checked: false } }, item.id);
        }
      });
    } else {
      items.forEach(item => {
        if (!selectedItems.includes(item.id)) {
          onChange({ target: { name, checked: true } }, item.id);
        }
      });
    }
  };
  
  const handleCheckboxChange = (e, itemId) => {
    onChange(e, itemId);
    setSearchTerm('');
  };
  
  const handleDelete = (itemId) => {
    onChange({ target: { name, checked: false } }, itemId);
  };

  const handleLayoutChange = (event, newLayout) => {
    if (newLayout !== null) {
      setCurrentLayout(newLayout);
    }
  };
  
  // Componente para renderizar un checkbox individual
  const CheckboxItem = ({ item }) => (
    <Box 
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        borderRadius: 1,
        border: '1px solid transparent',
        transition: 'all 0.2s',
        p: 0.5,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          borderColor: theme.palette.primary.light,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedItems.includes(item.id)}
            onChange={(e) => handleCheckboxChange(e, item.id)}
            name={name}
            size="small"
            color="primary"
            sx={{ 
              alignSelf: 'flex-start',
              mt: -1
            }}
          />
        }
        label={
          <Typography 
            variant="body2"
            sx={{
              width: '100%',
              fontSize: isSmallScreen ? '0.8rem' : '0.875rem',
              lineHeight: 1.5,
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {item.nombre}
          </Typography>
        }
        sx={{
          margin: 0,
          width: '100%',
          alignItems: 'flex-start',
          '& .MuiFormControlLabel-label': {
            flexGrow: 1,
          }
        }}
      />
    </Box>
  );
  
  // Renderizado en modo lista (sin virtualización)
  const ListLayout = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {filteredItems.map((item, index) => (
        <Box key={item.id}>
          <CheckboxItem item={item} />
          {index < filteredItems.length - 1 && <Divider sx={{ my: 0.5 }} />}
        </Box>
      ))}
    </Box>
  );

  // Renderizado en modo grid (columnas)
  const GridLayout = () => {
    const itemsPerColumn = Math.ceil(filteredItems.length / columns);
    const columnsData = [];
    
    for (let i = 0; i < columns; i++) {
      columnsData.push(
        filteredItems.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn)
      );
    }
    
    return (
      <Grid container spacing={2}>
        {columnsData.map((columnItems, colIndex) => (
          <Grid item xs={12 / columns} key={colIndex}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {columnItems.map((item, index) => (
                <Box key={item.id}>
                  <CheckboxItem item={item} />
                  {index < columnItems.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </Box>
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Grid item xs={12}>
      {title && (
        <Typography 
          variant="subtitle1" 
          gutterBottom 
          fontWeight="medium" 
          color={error ? "error" : "text.primary"}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {title}
          {required && <Typography color="error" sx={{ ml: 0.5 }}>*</Typography>}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Buscar..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.light,
                }
              }
            }}
          />
          
          {showLayoutToggle && (
            <ToggleButtonGroup
              value={currentLayout}
              exclusive
              onChange={handleLayoutChange}
              size="small"
              sx={{ height: 40 }}
            >
              <ToggleButton value="list" aria-label="vista lista">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="vista grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        
        {showSelectAll && (
          <Button
            size="small"
            startIcon={allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            onClick={handleSelectAll}
            color={allSelected ? "primary" : "primary"}
            variant="outlined"
            sx={{ 
              alignSelf: 'flex-start', 
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem'
            }}
          >
            {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
          </Button>
        )}
      </Box>
      
      <Paper 
        elevation={2} 
        sx={{
          height: 300,
          overflow: 'auto',
          padding: 1,
          borderRadius: 2,
          border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
          mt: 1,
          '& .MuiDivider-root:last-child': {
            display: 'none'
          }
        }}
      >
        {currentLayout === 'list' ? <ListLayout /> : <GridLayout />}
      </Paper>
      
      {error && helperText && (
        <Typography 
          variant="caption" 
          color="error" 
          sx={{ mt: 0.5, ml: 1 }}
        >
          {helperText}
        </Typography>
      )}
      
      <Box 
        sx={{ 
          mt: 2, 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          maxHeight: 100, 
          overflow: 'auto',
          p: 1,
          borderRadius: 1,
          backgroundColor: selectedItemsData.length > 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent'
        }}
      >
        {selectedItemsData.length > 0 ? (
          selectedItemsData.map(item => (
            <Tooltip 
              key={item.id} 
              title={item.nombre} 
              arrow
              placement="top"
              enterDelay={300}
              leaveDelay={200}
            >
              <Chip
                label={
                  <Typography 
                    noWrap 
                    sx={{
                      maxWidth: {
                        xs: '100px',
                        sm: '150px',
                        md: '200px'
                      },
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem'
                    }}
                  >
                    {item.nombre}
                  </Typography>
                }
                onDelete={() => handleDelete(item.id)}
                color="primary"
                size="small"
                sx={{
                  height: 'auto',
                  '& .MuiChip-label': { py: 0.5 },
                  borderRadius: 1.5
                }}
              />
            </Tooltip>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Ningún elemento seleccionado
          </Typography>
        )}
      </Box>
    </Grid>
  );
});

export default CheckboxGroup;